import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import questionService from '@/api/question'
import { QUESTION_STATUS } from '@/constants/marketBackendEnums'
// import { useActiveCurrency } from '@/hooks'
import { i18n } from '@/locale/i18n'
import useI18n from '@/hooks/useI18n'
import { questionDetailUrl, keepQueryString } from '@/urls'
import './QuestionHeader.scss'
// import constants from 'app/constants'

import Elipse from '@/assets/icons/elipse'
import Chip from '@/components/ui/chip/index.js'
import Thumbnail from '@/components/ui/thumbnail'

import OverlayPanel from '../../../components/OverlayPanel/OverlayPanel'
import dayjs from 'dayjs'
import currencies from '@/util/currencies'
import DeferredNumberFlow from '@/components/ui/deferred-number-flow'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import { isSportsEvent } from '@/features/market/utils/sportsEvent'
import { isCryptoPriceEvent, getMarketPageTitle, hasCryptoPriceTarget } from '@/features/market/utils/cryptoEvent'
import EventCountdown from '@/features/market/event-countdown'
import CryptoAssetBadge, { hasCryptoAssetBadge } from '@/features/market/crypto-asset-badge'
import CryptoPriceQuestionHeader from './CryptoPriceQuestionHeader'
import SportEventQuestionHeader from './SportEventQuestionHeader'
import SportEventMatchHeader from './SportEventMatchHeader'

const QuestionHeader = ({
  question,
  showBrierScore = false,
  inMarketPage,
  placebetOpened,
  isRealMoney = false,
  locale,
  footer,
  usesButtonsDisplay = false,
  shareFollow,
  categories,
  prioritizeLcpImage = false,
  leading = null,
  ...props
}) => {
  useI18n()
  const isMobileSize = useIsMobileSize()
  const renderPaused = () => {
    return renderMarketStateTemplate(
      'primary',
      <div>
        <OverlayPanel
          customPosition={{ top: '20px', left: 0 }}
          className="border border-solid border-Neutral-3 text-Neutral-6 font-normal !w-[250px] !p-4"
          hideDelay={400}
          content={i18n('bet.paused_info')}>
          <span>{i18n('bet.reviewing')}</span>
        </OverlayPanel>
      </div>
    )
  }
  const renderPending = () => {
    return renderMarketStateTemplate(
      'neutral',
      <div>
        <OverlayPanel
          customPosition={{ top: '20px', left: 0 }}
          className="border border-solid border-Neutral-3 text-Neutral-6 font-normal !w-[250px] !p-4"
          hideDelay={400}
          content={i18n('bet.pending_info')}>
          <span>{i18n('bet.pending_result')}</span>
        </OverlayPanel>
      </div>
    )
  }
  const renderCancled = () => {
    return renderMarketStateTemplate(
      'negative',
      i18n('question_card.date.cancelled') +
      ' ' +
      i18n('question_card.sufix_start_date_date').toLowerCase() +
      formatDate(question.bet_end_date)
    )
  }
  const renderResolved = () => {
    return renderMarketStateTemplate(
      'primary',
      `${i18n('question_card.date.resolved_on')} ${formatDate(question.resolve_date)}`
    )
  }
  const renderLiveBadge = () => {
    return renderMarketStateTemplate('informative', i18n('question_card.live_now'))
  }
  const renderNewBadge = () => {
    if (inMarketPage) {
      return (
        <div className="question-header__market-state relative question-header__market-state--informative">
          {i18n('common.new')}
        </div>
      )
    }
    return renderMarketStateTemplate('informative', i18n('common.new'))
  }
  // const renderBetStart = () => {
  //   return renderMarketStateTemplate('primary', formatDate(question.event_start_date))
  // }
  const renderBetEnd = () => {
    return (
      <div className="question-card__bet-end-date text-Neutral-4 text-xs font-medium">
        {formatDate(question.bet_end_date)}
      </div>
    )
  }

  const renderMarketStateTemplate = (color, text) => {
    return (
      <>
        <div
          className={classNames(
            'question-header__market-state',
            'relative',
            'question-header__market-state--' + color
          )}>
          <Elipse fillcolor="currentColor" width="8px" />
          {text}
        </div>
      </>
    )
  }

  // const renderBrierScore = () => {
  //   return (
  //     <div
  //       className="question-header__brier-score"
  //       onClick={() => (window.innerWidth <= 1024 ? setShowTooltip(!showTooltip) : null)}>
  //       <strong>{i18n('question_card.brier_score')}</strong> {question.brier_score}
  //     </div>
  //   )
  // }

  const formatDate = date => {
    const now = dayjs()
    const bedDate = dayjs(date)

    // Check if the input date is valid
    if (!bedDate.isValid()) {
      return 'Invalid date'
    }

    const diffInSeconds = bedDate.diff(now, 'second')

    const diffInMinutes = Math.floor(diffInSeconds / 60)
    const diffInHours = Math.floor(diffInMinutes / 60)

    // If date > 24 hours or If the date is in the past, return formatted date
    if (diffInHours > 24 || diffInSeconds < 0) {
      return bedDate.format('MMM D, YYYY')
    }

    // If date is between 1 and 24 hours, show time in hours
    if (diffInHours >= 1) {
      return i18n('question_card.date.bet_end_date_in_hours', { hours: diffInHours })
    }

    // If date is less than 1 hour but >= 1 minute, show time in minutes
    if (diffInMinutes >= 1) {
      return i18n('question_card.date.bet_end_date_in_minutes', { minutes: diffInMinutes })
    }

    // If date is less than 1 minute, show time in seconds
    const minutes = String(Math.floor(diffInSeconds / 60)).padStart(2, '0')
    const seconds = String(diffInSeconds % 60).padStart(2, '0')

    return i18n('question_card.date.bet_end_date_in_minutes_and_seconds', {
      minutes: minutes,
      seconds: seconds,
    })
  }

  // List/feed payloads can have status resolved before is_wagerable flips false.
  const isResolved = question?.status === QUESTION_STATUS.RESOLVED || questionService.isResolved(question)
  const isPaused = questionService.isPaused(question)
  const isPending = questionService.isWaitingForResults(question)
  const isCancelled = questionService.isCancelled(question)
  const isLive = questionService.isLive(question) && !isPaused && !isPending
   // The sports detail page leads with the fixture block (teams, score/kickoff, live clock),
  // so the badges and title that would repeat it are suppressed there.
  const isSportsDetail = isSportsEvent(question) && inMarketPage
  // Desktop puts the fixture above the stats row; mobile keeps it inside the card
  // below the stats row (see MarketCard).
  const showMatchHeader = isSportsDetail && !isMobileSize
  const showLiveBadge = isLive && !isSportsDetail

  // Check if any status badge should be shown
  const hasStatusBadge = isPaused || isPending || isResolved || isCancelled || isLive

  // Show "new" badge only if no status badge is shown
  // On the market detail page it's rendered above the title instead, see shouldShowNew usage below
  const shouldShowNew = question.is_new && !hasStatusBadge && !isSportsDetail

  const shouldShowPlayMoneyOnly = question.real_currency_available === false && inMarketPage && isRealMoney

  const renderPlayMoneyOnlyBadge = () => {
    return (
      <Chip
        text={i18n('common.play_money_only')}
        className="w-fit rounded-[500px] !bg-[var(--color-warning-2)] !text-sm !text-[var(--color-warning-6)] py-2 px-3 font-bold leading-[120%]"
      />
    )
  }

  const renderMarketStatus = () => {
    // Show bet end date only when appropriate. Non-sports markets (e.g. Bitcoin Up/Down)
    // stay "live" for the whole trading day, so only sports events (which show their own
    // live fixture clock) should hide the date while live.
    const shouldShowBetEnd =
      !inMarketPage &&
      question.bet_end_date &&
      !isResolved &&
      !isCancelled &&
      !(isLive && isSportsEvent(question))

    // Only show resolved/cancelled badges when not showing brier score
    const showResolved = isResolved && !showBrierScore
    const showCancelled = isCancelled && !showBrierScore

    return (
      <div className="question-card__market-status flex items-center gap-2">
        {shouldShowBetEnd && renderBetEnd()}

        {/* Status badges (priority order) */}
        {isPaused && renderPaused()}
        {isPending && renderPending()}
        {showResolved && renderResolved()}
        {showCancelled && renderCancled()}
        {showLiveBadge && renderLiveBadge()}

        {/* New badge - only show if no status badge, and not already shown above the title */}
        {!inMarketPage && shouldShowNew && renderNewBadge()}
      </div>
    )
  }

  // Detail page: the asset tile sits beside the title.
  const showCryptoAssetBadge = inMarketPage && isCryptoPriceEvent(question) && hasCryptoAssetBadge(question)

  const renderMajorOutcome = question => {
    const outcomes = question.outcomes
    const majorOutcome = outcomes[0]
    // The market page leads with the title alone; the thumbnail is a feed/card affordance.
    if (majorOutcome && !inMarketPage) {
      // LCP Optimization: Add explicit width/height and recommend preloading this image in index.html
      // Example preload: <link rel="preload" as="image" href="/path/to/your/majorOutcomeImage.webp" />
      return (
        <Thumbnail
          className="rounded-[var(--radius-xs)] w-10 h-10"
          image={majorOutcome.thumbnail}
          alt={majorOutcome.title}
          variant="rect"
          // First-row feed cards: match SSR preload and avoid competing with lazy decode.
          eager={prioritizeLcpImage}
        />
      )
    }
    return null
  }

  const renderYesPercentage = () => {
    if (
      question &&
      question.outcomes &&
      question.outcomes.length > 0 &&
      usesButtonsDisplay &&
      !isResolved
    ) {
      const yesOutcome =
        question.outcomes.find(outcome => outcome.title.toLowerCase() === 'yes') || question.outcomes[0]
      if (
        yesOutcome &&
        yesOutcome.price &&
        yesOutcome.price[!isRealMoney ? currencies.OOM.id : question.canonical_currency] !== undefined
      ) {
        const value = yesOutcome.price[!isRealMoney ? currencies.OOM.id : question.canonical_currency]
        const [colorClass, setColorClass] = useState('')
        const prevValueRef = useRef(value)

        useEffect(() => {
          // Skip initial render
          if (prevValueRef.current !== value) {
            // Determine if value increased or decreased
            if (value > prevValueRef.current) {
              setColorClass('text-positive-4')
            } else if (value < prevValueRef.current) {
              setColorClass('text-negative-4')
            }

            // Reset color after 1 second
            const timer = setTimeout(() => {
              setColorClass('')
            }, 1000)

            // Update the ref for the next comparison
            prevValueRef.current = value

            return () => clearTimeout(timer)
          }
        }, [value])

        return (
          <div
            className={`min-w-[43px] w-[43px] whitespace-nowrap text-xl font-bold leading-[120%] absolute right-0 top-0 transition-colors duration-300 flex justify-end ${colorClass || 'text-Neutral-6'}`}>
            <DeferredNumberFlow locales={locale} value={(100 * value).toFixed(2)} suffix="%" />
          </div>
        )
      }
    }
    return null
  }

  if (isSportsEvent(question) && !inMarketPage) {
    return <SportEventQuestionHeader question={question} />
  }

  // Crypto price cards lead with the live price against the target instead of a title.
  if (!inMarketPage && hasCryptoPriceTarget(question)) {
    return <CryptoPriceQuestionHeader question={question} leading={leading} />
  }

  // Crypto price up/down detail page: the title shares its row with a live countdown to the
  // event end, which replaces the closing-date line under the title on mobile.
  const showEventCountdown = inMarketPage && isCryptoPriceEvent(question) && Boolean(question.event_end_date)

  // Mobile has to give the countdown its 72px, so the title drops to 20px and clamps.
  const useCompactTitle = showEventCountdown && isMobileSize

  const displayTitle = inMarketPage ? getMarketPageTitle(question) : question.title

  const titleLink = (
    <Link to={keepQueryString(questionDetailUrl(question))}>
      {inMarketPage ? (
        <h1
          className={classNames(
            'question-card__title md:text-[32px] font-[number:var(--typography-label-lg-bold-weight)]',
            useCompactTitle
              ? 'text-xl leading-[1.33] line-clamp-2'
              : 'text-[length:var(--typography-label-lg-size)] leading-[var(--typography-label-lg-line-height)]'
          )}>
          {displayTitle}
        </h1>
      ) : (
        <h3
          className={`question-card__title text-sm font-semibold leading-5 line-clamp-3 hover:underline ${renderYesPercentage() ? 'pr-[45px]' : ''}`}>
          {question.title}
        </h3>
      )}
    </Link>
  )

  // Market page mobile keeps the closing date with the title instead of the stats row.
  const showBetEndUnderTitle =
    isMobileSize &&
    !useCompactTitle &&
    question.bet_end_date &&
    !isResolved &&
    !isCancelled &&
    !isSportsEvent(question)

  return (
    <header className={`w-full min-h-0 md:min-h-10 ${inMarketPage ? '' : 'max-h-[98px]'}`}>
      <div className="flex gap-3">
        {(!placebetOpened || isMobileSize) && renderMajorOutcome(question)}

        <div className="flex flex-col justify-start gap-1 w-full relative">
          {inMarketPage && (shouldShowNew || shouldShowPlayMoneyOnly) && (
            <div
              className={`question-card__title-badges flex items-center w-full gap-2 ${
                isMobileSize ? 'justify-start' : 'justify-between'
              }`}>
              {shouldShowNew && <div>{renderNewBadge()}</div>}
              {shouldShowPlayMoneyOnly && <div>{renderPlayMoneyOnlyBadge()}</div>}
            </div>
          )}

          <div className="question-card__title-row flex items-center w-full justify-between gap-3">
            {inMarketPage ? (
              <div className="flex flex-col gap-1 min-w-0">
                {isSportsDetail ? (
                  <h1 className="sr-only">{question.title}</h1>
                ) : showCryptoAssetBadge ? (
                  <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <CryptoAssetBadge
                      question={question}
                      className={useCompactTitle ? 'w-7 h-7' : 'w-8 h-8 md:w-10 md:h-10'}
                    />
                    {titleLink}
                  </div>
                ) : (
                  titleLink
                )}
                {showBetEndUnderTitle && renderBetEnd()}
                {/* Sports lead with the fixture panel, so the stats row takes the
                    title's place on this row, opposite share/follow. */}
                {isSportsDetail && footer}
              </div>
            ) : (
              titleLink
            )}
            {!inMarketPage && renderYesPercentage()}
            {(showEventCountdown || (inMarketPage && shareFollow)) && (
              <div className="question-card__title-actions flex flex-shrink-0 items-center gap-2">
                {showEventCountdown && <EventCountdown endDate={question.event_end_date} />}
                {inMarketPage && shareFollow}
              </div>
            )}
          </div>

          {showMatchHeader && (
            <div className="pb-4 pt-1">
              <SportEventMatchHeader question={question} categories={categories} />
            </div>
          )}

          {renderMarketStatus()}
          {!isSportsDetail && footer}
        </div>
      </div>
      {props.questionSummary}
    </header>
  )
}

export default QuestionHeader
